import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import CustomSpinner from '@components/Spinner/CustomSpinner';
import { useSubscribeToNewsletter, useUnsubscribeFromNewsletter } from '@hooks';
import { Newsletter } from '@hooks/useCurrentNewsletter';
import { highlight_path, subscribe_promotion_path, upgrade_subscription_path } from '@routes';

import styles from './styles.module.scss';

const DARK_MODE_SPINNER_COLOR = '#fff';

interface NewsletterSignupButtonProps {
  currentUserAccessLevel: number;
  isSubscribed: boolean;
  canSubscribe: boolean;
  newsletter: Newsletter;
}

const NewsletterSignupButton = ({
  currentUserAccessLevel,
  isSubscribed,
  canSubscribe,
  newsletter,
}: NewsletterSignupButtonProps) => {
  const { mutate: subscribeToNewsletter, isLoading: isFollowNewsletterLoading } =
    useSubscribeToNewsletter();
  const { mutate: unsubscribeFromNewsletter, isLoading: isUnFollowNewsletterLoading } =
    useUnsubscribeFromNewsletter();

  const [subscribeAction, setSubscribeAction] = useState<null | (() => void)>();
  const [buttonText, setButtonText] = useState<string>();
  const [redirectUri, setRedirectUri] = useState<string>();

  useEffect(() => {
    if (isSubscribed) {
      // User who already subscribes to this newsletter
      setButtonText('You are Subscribed');
      setSubscribeAction(() => () => unsubscribeFromNewsletter({ newsletter_id: newsletter.id }));
    } else if (canSubscribe) {
      // User who has enough access for this newsletter
      setButtonText(`Subscribe to ${newsletter.name}`);
      setSubscribeAction(() => () => subscribeToNewsletter({ newsletter_id: newsletter.id }));
    } else if (currentUserAccessLevel > 0) {
      // TI subscriber who doesn't have enough access to this newsletter (e.g. pro)
      setButtonText(`Upgrade to Subscribe to ${newsletter.name}`);
      setRedirectUri(upgrade_subscription_path());
    } else {
      // Lead who doesn't have enough access to this newsletter (e.g. pro)
      setButtonText('Subscribe');
      setRedirectUri(
        subscribe_promotion_path({
          offer: newsletter.offer?.code || 'yearly',
          redirect_to: highlight_path({ highlight: newsletter.slug }),
        })
      );
    }
  }, [
    currentUserAccessLevel,
    isSubscribed,
    canSubscribe,
    newsletter,
    subscribeToNewsletter,
    unsubscribeFromNewsletter,
  ]);

  const isLoading = isUnFollowNewsletterLoading || isFollowNewsletterLoading;

  return (
    <Button
      className={clsx(styles.toggleButton, {
        [styles.unsubscribeButton]: isSubscribed,
        [styles.subscribeButton]: !isSubscribed,
        'unsub-btn': isSubscribed,
        'sub-btn': !isSubscribed,
      })}
      onClick={() => {
        subscribeAction ? subscribeAction() : (window.location.href = redirectUri);
      }}
      disabled={isLoading}
    >
      {isLoading ? <CustomSpinner color={DARK_MODE_SPINNER_COLOR} size={10} /> : <>{buttonText}</>}
    </Button>
  );
};

export default NewsletterSignupButton;
